import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import Stack from "@mui/material/Stack";
import api from "../../services/api";
import { listBoards, showBoard } from "../../services/kanbanEnterpriseService";

const DIALOG_Z_INDEX = 3000;
const SELECT_MENU_Z_INDEX = 10005;

const selectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
  className: "flow-builder-select-menu-popover",
  style: { zIndex: SELECT_MENU_Z_INDEX },
  PaperProps: {
    className: "flow-builder-select-menu-paper",
    style: { zIndex: SELECT_MENU_Z_INDEX, maxHeight: 280 },
  },
};

const FlowBuilderKanbanLaneModal = ({ open, onSave, onUpdate, data, close }) => {
  const isMounted = useRef(true);
  const [activeModal, setActiveModal] = useState(false);
  const [kanbanMode, setKanbanMode] = useState("classic");
  const [classicLanes, setClassicLanes] = useState([]);
  const [classicTagId, setClassicTagId] = useState("");
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [enterpriseColumns, setEnterpriseColumns] = useState([]);
  const [enterpriseColumnId, setEnterpriseColumnId] = useState("");

  useEffect(() => {
    if (open === "edit" || open === "create") {
      const d = open === "edit" ? data?.data : {};
      setKanbanMode(d?.kanbanMode === "enterprise" ? "enterprise" : "classic");
      setClassicTagId(d?.classicLane?.tagId ?? "");
      setBoardId(d?.enterpriseLane?.boardId ?? "");
      setEnterpriseColumnId(d?.enterpriseLane?.columnId ?? "");
      setActiveModal(true);
      api.get("/tag/kanban").then((res) => setClassicLanes(res.data?.lista || [])).catch(() => setClassicLanes([]));
      listBoards().then((b) => setBoards(Array.isArray(b) ? b : b?.boards || [])).catch(() => setBoards([]));
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    if (!boardId) {
      setEnterpriseColumns([]);
      return;
    }
    showBoard(boardId)
      .then((b) => setEnterpriseColumns(b?.columns || []))
      .catch(() => setEnterpriseColumns([]));
  }, [boardId]);

  useEffect(() => () => { isMounted.current = false; }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const buildPayload = () => {
    if (kanbanMode === "classic") {
      const lane = classicLanes.find((t) => String(t.id) === String(classicTagId));
      return {
        kanbanMode: "classic",
        classicLane: lane ? { tagId: lane.id, name: lane.name } : null,
        enterpriseLane: null,
        removePreviousLane: true,
      };
    }
    const col = enterpriseColumns.find((c) => String(c.id) === String(enterpriseColumnId));
    const board = boards.find((b) => String(b.id) === String(boardId));
    return {
      kanbanMode: "enterprise",
      classicLane: null,
      enterpriseLane: col
        ? { boardId: board?.id, boardName: board?.name, columnId: col.id, name: col.name }
        : null,
      removePreviousLane: true,
    };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (kanbanMode === "classic" && !payload.classicLane) return;
    if (kanbanMode === "enterprise" && !payload.enterpriseLane) return;
    handleClose();
    if (open === "edit") {
      onUpdate({ ...data, data: payload });
    } else {
      onSave(payload);
    }
  };

  return (
    <Dialog
      open={activeModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      style={{ zIndex: DIALOG_Z_INDEX }}
      PaperProps={{
        style: {
          zIndex: DIALOG_Z_INDEX,
          position: "relative",
          overflow: "visible",
        },
      }}
      BackdropProps={{ style: { zIndex: DIALOG_Z_INDEX - 1, backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
    >
      <DialogTitle>{open === "edit" ? "Editar Kanban Lane" : "Adicionar Kanban Lane"}</DialogTitle>
      <Stack spacing={2} sx={{ px: 2, pb: 2, overflow: "visible" }}>
        <Typography variant="body2" color="textSecondary">
          Move o ticket do contato para uma lane do Kanban durante o fluxo.
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup value={kanbanMode} onChange={(e) => setKanbanMode(e.target.value)}>
            <FormControlLabel value="classic" control={<Radio color="primary" />} label="Kanban Clássico (tags)" />
            <FormControlLabel value="enterprise" control={<Radio color="primary" />} label="Kanban Enterprise (colunas)" />
          </RadioGroup>
        </FormControl>

        {kanbanMode === "classic" && (
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Lane clássica</InputLabel>
            <Select
              value={classicTagId}
              onChange={(e) => setClassicTagId(e.target.value)}
              label="Lane clássica"
              MenuProps={selectMenuProps}
            >
              {classicLanes.map((lane) => (
                <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {kanbanMode === "enterprise" && (
          <>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Board</InputLabel>
              <Select
                value={boardId}
                onChange={(e) => { setBoardId(e.target.value); setEnterpriseColumnId(""); }}
                label="Board"
                MenuProps={selectMenuProps}
              >
                {boards.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" fullWidth disabled={!boardId}>
              <InputLabel>Coluna (lane)</InputLabel>
              <Select
                value={enterpriseColumnId}
                onChange={(e) => setEnterpriseColumnId(e.target.value)}
                label="Coluna (lane)"
                MenuProps={selectMenuProps}
              >
                {enterpriseColumns.map((col) => (
                  <MenuItem key={col.id} value={col.id}>{col.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Stack>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" variant="outlined">Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderKanbanLaneModal;
